import { Reducer, Dispatch, useReducer, useMemo } from 'react';
import { SideNavCategory } from '../../../components/side-nav';
import { ReportField } from '../../../models/reportField';
import { createReportFieldKey } from '../../../util';
import {
  getAndMapReportFieldToSideNavCategories,
  buildSideNavCategories,
  filterReportFields,
} from '../helpers';

interface ReportCategoryState {
  standardFields?: SideNavCategory<ReportField>[];
  favoriteFields?: SideNavCategory<ReportField>[];
  recentFields?: SideNavCategory<ReportField>[];
  categories?: SideNavCategory<ReportField>[];
  selectedCategory?: SideNavCategory<ReportField>;
  selectedSubCategory?: SideNavCategory<ReportField>;
  ignoreReportFieldIds?: Set<string>;
  reportFieldsMap: Map<string, ReportField>;
  favoriteFieldsMap: Map<string, ReportField>;
}

interface IUpdateStandardFieldsPayload {
  reportFields: Array<ReportField>;
  selectedCategoryName?: string;
} 

type ReportCategoryAction =
  | {
      type: 'selectCategory';
      payload: SideNavCategory<ReportField>;
    }
  | {
      type: 'selectSubCategory';
      payload: SideNavCategory<ReportField>;
    }
  | {
      type: 'filterSubCategoryByRuleBuilderFields';
      payload: Array<ReportField>;
    }
  | {
      type: 'updateStandardFields';
      payload: IUpdateStandardFieldsPayload;
    }
  | {
      type: 'updateFavoriteFields';
      payload: Array<ReportField>;
    }
  | {
      type: 'updateRecentFields';
      payload: Array<ReportField>;
    };

const standardFields: SideNavCategory<ReportField>[] = [];
const favoriteFields: SideNavCategory<ReportField>[] = [];
const recentFields: SideNavCategory<ReportField>[] = [];

const findSelectedCategory = (
  categories: SideNavCategory<ReportField>[],
  selectedCategory: SideNavCategory<ReportField>,
): SideNavCategory<ReportField> =>
  categories[0].items.find(
    (item) => item.key === selectedCategory.key,
  ) as SideNavCategory<ReportField>;

const categories = buildSideNavCategories(
  favoriteFields,
  recentFields,
  standardFields,
);
const initialState: ReportCategoryState = {
  standardFields,
  recentFields,
  favoriteFields,
  categories,
  reportFieldsMap: new Map(),
  favoriteFieldsMap: new Map(),
  ignoreReportFieldIds: new Set(),
  selectedCategory: categories[0].items[0] as SideNavCategory<ReportField>,
};

const reducer: Reducer<ReportCategoryState, ReportCategoryAction> = (
  prevState,
  action,
): ReportCategoryState => {
  switch (action.type) {
    case 'selectCategory':
      return {
        ...prevState,
        selectedCategory: action.payload,
      };

    case 'selectSubCategory':
      return {
        ...prevState,
        selectedSubCategory: action.payload,
      };

    case 'filterSubCategoryByRuleBuilderFields':
      const ignoreReportFieldIds = new Set(
        action.payload.map((field) => createReportFieldKey(field)),
      );

      const {
        favoriteFields,
        recentFields,
        standardFields,
        selectedCategory,
      } = prevState;

      const categories = buildSideNavCategories(
        filterReportFields(favoriteFields, ignoreReportFieldIds),
        filterReportFields(recentFields, ignoreReportFieldIds),
        filterReportFields(standardFields, ignoreReportFieldIds),
      );
      const nextSelectCategory = findSelectedCategory(
        categories,
        selectedCategory,
      );

      return {
        ...prevState,
        categories,
        ignoreReportFieldIds,
        selectedCategory: nextSelectCategory as SideNavCategory<ReportField>,
      };
    case 'updateStandardFields': {
      const standardFields = getAndMapReportFieldToSideNavCategories(
        action.payload.reportFields,
      );
      const { favoriteFields, recentFields, ignoreReportFieldIds } = prevState;
      const categories = buildSideNavCategories(
        filterReportFields(favoriteFields, ignoreReportFieldIds),
        filterReportFields(recentFields, ignoreReportFieldIds),
        filterReportFields(standardFields, ignoreReportFieldIds),
      );

      const reportFieldsMap = new Map(
        action.payload.reportFields.map((field) => [createReportFieldKey(field), field]),
      );

      const focusCategory = action.payload.selectedCategoryName ? 
        categories[0].items?.find(c => c.name ===  action.payload.selectedCategoryName) as SideNavCategory<ReportField>
        :
        categories[0].items[0] as SideNavCategory<ReportField>;

      return {
        ...prevState,
        categories,
        reportFieldsMap,
        selectedCategory: focusCategory,
        standardFields: standardFields,
      };
    }

    case 'updateFavoriteFields': {
      const {
        standardFields,
        recentFields,
        ignoreReportFieldIds,
        selectedCategory,
      } = prevState;

      const { favoriteFields, favoriteFieldsMap } = action.payload?.reduce(
        (acc, field) => {
          // rebuild favorite sideNav Items
          const fieldKey = createReportFieldKey(field);
          acc.favoriteFields.push({
            key: fieldKey,
            name: field.fieldName,
            tag: { ...field, err: '' },
            typeName: 'favoriteFields',
          });
          // rebuild favorite fields map for reusing
          acc.favoriteFieldsMap.set(fieldKey, field);

          return acc;
        },
        {
          favoriteFields: [] as SideNavCategory<ReportField>[],
          favoriteFieldsMap: new Map<string, ReportField>(),
        },
      );

      const categories = buildSideNavCategories(
        filterReportFields(favoriteFields, ignoreReportFieldIds),
        filterReportFields(recentFields, ignoreReportFieldIds),
        filterReportFields(standardFields, ignoreReportFieldIds),
      );
      const nextSelectCategory = findSelectedCategory(
        categories,
        selectedCategory,
      );
      return {
        ...prevState,
        favoriteFields,
        favoriteFieldsMap,
        categories,
        selectedCategory: nextSelectCategory,
      };
    }

    case 'updateRecentFields': {
      const recentFields: SideNavCategory<ReportField>[] = action.payload.map(
        (field) => ({
          key: createReportFieldKey(field),
          name: field.fieldName,
          tag: { ...field, err: '' },
          typeName: 'recentFields',
        }),
      );
      const {
        standardFields,
        favoriteFields,
        ignoreReportFieldIds,
        selectedCategory,
      } = prevState;
      const categories = buildSideNavCategories(
        filterReportFields(favoriteFields, ignoreReportFieldIds),
        filterReportFields(recentFields, ignoreReportFieldIds),
        filterReportFields(standardFields, ignoreReportFieldIds),
      );
      const nextSelectCategory = findSelectedCategory(
        categories,
        selectedCategory,
      );
      return {
        ...prevState,
        recentFields,
        categories,
        selectedCategory: nextSelectCategory,
      };
    }

    default:
      throw new Error('unhandle action');
  }
};

interface ReportCategoryDispatchers {
  setSelectedCategory: (payload: SideNavCategory<ReportField>) => void;
  setSelectedSubCategory: (payload: SideNavCategory<ReportField>) => void;
  filterSubCategoryByRuleBuilderFields: (payload: Array<ReportField>) => void;
  updateStandardFields: (payload: IUpdateStandardFieldsPayload) => void;
  updateFavoriteFields: (payload: Array<ReportField>) => void;
  updateRecentFields: (payload: Array<ReportField>) => void;
}

const buildDispatchers = (
  dispatch: Dispatch<ReportCategoryAction>,
): ReportCategoryDispatchers => ({
  setSelectedCategory: (payload: SideNavCategory<ReportField>): void =>
    dispatch({
      type: 'selectCategory',
      payload,
    }),
  setSelectedSubCategory: (payload: SideNavCategory<ReportField>): void =>
    dispatch({
      type: 'selectSubCategory',
      payload,
    }),
  filterSubCategoryByRuleBuilderFields: (payload: Array<ReportField>): void =>
    dispatch({
      type: 'filterSubCategoryByRuleBuilderFields',
      payload,
    }),
  updateStandardFields: (payload: IUpdateStandardFieldsPayload): void =>
    dispatch({ type: 'updateStandardFields', payload }),
  updateFavoriteFields: (payload: Array<ReportField>): void =>
    dispatch({ type: 'updateFavoriteFields', payload }),
  updateRecentFields: (payload: Array<ReportField>): void =>
    dispatch({ type: 'updateRecentFields', payload }),
});

function useReportCategoryReducer(): [
  ReportCategoryState,
  ReportCategoryDispatchers,
] {
  const [state, dispatch] = useReducer(reducer, initialState);
  const dispatchers = useMemo(() => buildDispatchers(dispatch), []);

  return [state, dispatchers];
}

export { useReportCategoryReducer };
